import React, { useState, useEffect } from 'react';
import { propOr, path } from 'ramda';
import { useQuery, useMutation } from '@apollo/react-hooks';
import Loader from 'react-loader-spinner';
import {
  AppProvider,
  Button,
  Card,
  ButtonGroup,
  Form,
  Frame,
  Icon,
  Stack,
  Layout,
  Page,
  Select,
  Badge,
  RangeSlider,
  Toast,
  Modal,
  TextContainer,
  Heading,
  FormLayout,
} from '@shopify/polaris';
import { StoreMajorMonotone, CashDollarMajorMonotone, ClockMajorMonotone, LocationMajorMonotone } from '@shopify/polaris-icons';
import SuccessBanner from '../components/SuccessBanner';
import { GET_CAREER_PORTAL, GET_CURRENT_SUBSCRIPTION } from '../apollo/queries';
import { CREATE_SHOP_CAREER_PORTAL, UPDATE_SHOP_CAREER_PORTAL } from '../apollo/mutation';
import { FONT_WEIGHT_OPTIONS, FONT_FAMILY_OPTIONS, FONT_CASING_OPTIONS } from '../../utils/formDefaultValues';

import ColorPicker from '../components/ColorPicker';

import { defaultWhiteColor, defaultDarkGrayColor, hslaColor } from '../../utils/defaultColors';
import { keysToCamel } from '../../utils/normalizer';

const JobDescriptionPage = ({ history, shopifyToken, shopifyDomain }) => {
  const { push: changeRoute } = history;
  const queryAttr = { variables: { shopifyToken } };

  const [createShopCareerPortal] = useMutation(CREATE_SHOP_CAREER_PORTAL);
  const [updateShopCareerPortal] = useMutation(UPDATE_SHOP_CAREER_PORTAL);

  const { loading, data, refetch } = useQuery(GET_CAREER_PORTAL, queryAttr);
  const { data: subscriptionData } = useQuery(GET_CURRENT_SUBSCRIPTION, queryAttr);

  const shop = propOr({}, 'shop')(subscriptionData);
  const careerPortalSet = propOr(false, 'careerPortalSet')(shop);
  const isCareerPortalEmpty = !careerPortalSet;

  const [form, setForm] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [preview, setPreview] = useState(false);
  const [showSuccessBanner, setShowSuccessBanner] = useState(false);
  const [isSaveBarActive, setIsSaveBarActive] = useState(false);
  const [toastStatus, setToast] = useState({ isOpen: false, text: '' });
  const togglePreviewModal = () => setPreview(!preview);

  useEffect(() => {
    const careerPortal = propOr({}, 'careerPortal')(data);
    const styleControls = propOr({}, 'styleControls')(careerPortal);
    const jobDescriptionPage = propOr({}, 'job_description_page')(styleControls);
    const styleControlsFormatted = keysToCamel(careerPortal.styleControls);
    const talentCommunitySection = propOr(false, 'talentCommunitySection')(careerPortal);

    setForm((prevForm) => ({
      ...prevForm,
      ...styleControlsFormatted,
      jobDescriptionPage: {
        ...prevForm.jobDescriptionPage,
        enabled: true,
        backgroundColor: jobDescriptionPage?.background_color || defaultWhiteColor,
        headingColor: jobDescriptionPage?.heading_color || defaultDarkGrayColor,
        bodyColor: jobDescriptionPage?.body_color || defaultDarkGrayColor,
        buttonColor: jobDescriptionPage?.button_color || defaultDarkGrayColor,
        buttonTextColor: jobDescriptionPage?.button_text_color || defaultWhiteColor,
        formBorderColor: jobDescriptionPage?.form_border_color || defaultWhiteColor,
        topSpacing: jobDescriptionPage?.top_spacing || '10px',
        sideSpacing: jobDescriptionPage?.side_spacing || '10px',
        buttonTextWeight: jobDescriptionPage?.button_text_weight || '300',
        buttonTextCasing: jobDescriptionPage?.button_text_casing || 'uppercase',
        buttonFontFamily: jobDescriptionPage?.button_font_family || 'inherit',
      },
      talentCommunityStyles: {
        ...styleControlsFormatted?.talentCommunitySection,
      },
      talentCommunitySection,
    }));
  }, [data]);

  if (loading || isLoading) {
    return (
      <div className='loader-container'>
        <Loader type='TailSpin' color='#6371c7' height={80} width={80} />
      </div>
    );
  }

  const updateFontValue =
    ({ sectionName, fieldName }) =>
    (value) => {
      setIsSaveBarActive(true);
      setForm({
        ...form,
        [sectionName]: {
          ...form[sectionName],
          [fieldName]: `${value}`,
        },
      });
    };

  const updateColor = (fieldName) => (color) => {
    setIsSaveBarActive(true);
    setForm({
      ...form,
      jobDescriptionPage: {
        ...form.jobDescriptionPage,
        [fieldName]: color,
      },
    });
  };

  const toggleToast = (text) =>
    setToast((prev) => ({
      isOpen: !prev.isOpen,
      text: prev.isOpen ? prev.text : text,
    }));

  const onSuccess = () => {
    refetch();
    setIsSaveBarActive(false);
    setShowSuccessBanner(true);
    setIsLoading(false);
  };

  const onError = (e) => {
    let messages = path(['graphQLErrors', 0, 'message'])(e);
    toggleToast(`Error: ${messages}`);
    setIsLoading(false);
  };

  const onSubmit = (e) => {
    e?.preventDefault();
    setPreview(false);
    setIsLoading(true);
    const careerPortal = propOr({}, 'careerPortal')(data);
    const save = isCareerPortalEmpty ? createShopCareerPortal : updateShopCareerPortal;
    const variables = {
      ...careerPortal,
      ...form,
      shopifyToken,
    };
    delete variables.styleControls;

    save({ variables }).then(onSuccess).catch(onError);
  };

  const toastMarkup = toastStatus.isOpen ? <Toast content={toastStatus.text} onDismiss={toggleToast} /> : null;

  return (
    <AppProvider
      i18n={{
        Polaris: {
          DropZone: {
            actionTitle: 'Add image',
          },
        },
      }}>
      <Frame>
        <Page
        breadcrumbs={[{ content: 'Career Portal', onAction: () => { changeRoute('/career-portal-index'); } }]}
          title='Job description page'
          titleMetadata={<Badge status={'success'}>Published</Badge>}
          pagination={{
            hasPrevious: !isSaveBarActive,
            hasNext: !isSaveBarActive,
            nextURL: !isSaveBarActive ? `/career-portal/post-apply-page` : null,
            previousURL: !isSaveBarActive ? `/career-portal/talent-community-section` : null,
            nextTooltip: 'Next',
            previousTooltip: 'Previous',
          }}
          primaryAction={
            <ButtonGroup>
              <Button plain monochrome onClick={togglePreviewModal}>
                Preview
              </Button>
              <Button primary onClick={onSubmit} disabled={!isSaveBarActive}>
                Publish
              </Button>
            </ButtonGroup>
          }>
          <Form onSubmit={onSubmit}>
            {showSuccessBanner && (
              <SuccessBanner
                history={history}
                title='Job description page updated'
                onDismiss={() => setShowSuccessBanner(false)}
                portalBody={'View on your career portal'}
                detailsBody={'go to all career portal sections'}
                portalUrl={`https://${shopifyDomain}/a/careers/`}
                detailsUrl={'/career-portal-index'}
              />
            )}
            <Layout>
              <Layout.AnnotatedSection
                title='Appearance'
                description='Customize the font and page background colors to match your store’s theme.'>
                <Card sectioned>
                  <Card.Section title='PAGE'>
                    <FormLayout>
                      <ColorPicker
                        colorName='Background color'
                        defaultColor={form.jobDescriptionPage?.backgroundColor}
                        onSelectColor={updateColor('backgroundColor')}
                      />
                    </FormLayout>
                    <br />
                    <FormLayout>
                      <div style={{ width: '60%' }}>
                        <RangeSlider
                          label='Top spacing'
                          value={form.jobDescriptionPage?.topSpacing}
                          onChange={(val) => updateColor('topSpacing')(`${val}`)}
                          output
                          max={100}
                          min={0}
                          suffix={`${form.jobDescriptionPage?.topSpacing}%`}
                        />
                        <RangeSlider
                          label='Side spacing'
                          value={form.jobDescriptionPage?.sideSpacing}
                          onChange={(val) => updateColor('sideSpacing')(`${val}`)}
                          output
                          max={100}
                          min={0}
                          suffix={`${form.jobDescriptionPage?.sideSpacing}%`}
                        />
                      </div>
                    </FormLayout>
                  </Card.Section>
                  <Card.Section title='JOB HEADER'>
                    <ColorPicker
                      colorName='Job header color'
                      defaultColor={form.jobDescriptionPage?.headingColor}
                      onSelectColor={updateColor('headingColor')}
                    />
                  </Card.Section>
                  <Card.Section title='JOB CONTENT'>
                    <ColorPicker
                      colorName='Job text color'
                      defaultColor={form.jobDescriptionPage?.bodyColor}
                      onSelectColor={updateColor('bodyColor')}
                    />
                  </Card.Section>
                  <Card.Section title='APPLICATION FORM'>
                    <ColorPicker
                      colorName='Application form border color'
                      defaultColor={form.jobDescriptionPage?.formBorderColor}
                      onSelectColor={updateColor('formBorderColor')}
                    />
                    <ColorPicker
                      colorName='Submit application button color'
                      defaultColor={form.jobDescriptionPage?.buttonColor}
                      onSelectColor={updateColor('buttonColor')}
                    />
                    <ColorPicker
                      colorName='Submit application button text color'
                      defaultColor={form.jobDescriptionPage?.buttonTextColor}
                      onSelectColor={updateColor('buttonTextColor')}
                    />
                    <div style={{ width: '50%' }}>
                      <Select
                        label='Button font family'
                        options={FONT_FAMILY_OPTIONS}
                        onChange={updateFontValue({ sectionName: 'jobDescriptionPage', fieldName: 'buttonFontFamily' })}
                        value={form.jobDescriptionPage?.buttonFontFamily}
                      />
                    </div>
                    <div style={{ width: '50%' }}>
                      <Select
                        label='Button text font weight'
                        options={FONT_WEIGHT_OPTIONS}
                        onChange={updateFontValue({ sectionName: 'jobDescriptionPage', fieldName: 'buttonTextWeight' })}
                        value={form.jobDescriptionPage?.buttonTextWeight}
                      />
                    </div>
                    <div style={{ width: '50%' }}>
                      <Select
                        label='Button text casing'
                        options={FONT_CASING_OPTIONS}
                        onChange={updateFontValue({ sectionName: 'jobDescriptionPage', fieldName: 'buttonTextCasing' })}
                        value={form.jobDescriptionPage?.buttonTextCasing}
                      />
                    </div>
                  </Card.Section>
                </Card>
              </Layout.AnnotatedSection>
            </Layout>
            <div className='career-portal__form-buttons'>
              <Button onClick={() => changeRoute('/career-portal-index')}>Cancel</Button>
              <Button primary submit disabled={!isSaveBarActive}>
                Publish
              </Button>
            </div>
          </Form>
          <Modal
            open={!!preview}
            large
            onClose={togglePreviewModal}
            primaryAction={{
              content: 'Publish',
              disabled: !isSaveBarActive,
              onAction: () => {
                onSubmit();
              },
            }}
            secondaryActions={[
              {
                content: 'Cancel',
                onAction: togglePreviewModal,
              },
            ]}
            title={
              <TextContainer>
                <Heading>Preview job description page</Heading>
                <p style={{ fontSize: '0.85em' }}>
                  Your theme fonts are not applied in the preview bellow, publish the section to your career portal to see how the
                  section will look on your store
                </p>
              </TextContainer>
            }
            loading={false}>
            <div className={'preview__modal'}>
              <div
                className='job_description_wrapper'
                style={{
                  backgroundColor: `${hslaColor(form.jobDescriptionPage?.backgroundColor, '#fff')}`,
                }}>
                <div className='shophire_header'>
                  <h1
                    className='heading_text'
                    style={{
                      color: `${hslaColor(form.jobDescriptionPage?.headingColor, '#3a3a3a')}`,
                    }}>
                    IT Coordinator
                  </h1>
                  <br />
                  <div className='header_info' style={{ marginTop: '20px' }}>
                    <Stack>
                      <div
                        className='header_info_item'
                        style={{
                          color: `${hslaColor(form.jobDescriptionPage?.bodyColor, '#3a3a3a')}`,
                          fill: `${hslaColor(form.jobDescriptionPage?.bodyColor, '#3a3a3a')}`,
                        }}>
                        <Stack spacing={'tight'}>
                          <Icon source={StoreMajorMonotone} />
                          <span>ShopHire S.A.S</span>
                        </Stack>
                      </div>
                      <div
                        className='header_info_item'
                        style={{
                          color: `${hslaColor(form.jobDescriptionPage?.bodyColor, '#3a3a3a')}`,
                          fill: `${hslaColor(form.jobDescriptionPage?.bodyColor, '#3a3a3a')}`,
                        }}>
                        <Stack spacing={'tight'}>
                          <Icon source={LocationMajorMonotone} />
                          <span> Miami, Florida</span>
                        </Stack>
                      </div>
                      <div
                        className='header_info_item'
                        style={{
                          color: `${hslaColor(form.jobDescriptionPage?.bodyColor, '#3a3a3a')}`,
                          fill: `${hslaColor(form.jobDescriptionPage?.bodyColor, '#3a3a3a')}`,
                        }}>
                        <Stack spacing={'tight'}>
                          <Icon source={ClockMajorMonotone} />
                          <span>Full-Time</span>
                        </Stack>
                      </div>
                      <div
                        className='header_info_item'
                        style={{
                          color: `${hslaColor(form.jobDescriptionPage?.bodyColor, '#3a3a3a')}`,
                          fill: `${hslaColor(form.jobDescriptionPage?.bodyColor, '#3a3a3a')}`,
                        }}>
                        <Stack spacing={'tight'}>
                          <Icon source={CashDollarMajorMonotone} />
                          <span>$4000 per month</span>
                        </Stack>
                      </div>
                    </Stack>
                  </div>
                </div>
                <div className='job_content' id='job_content'>
                  <div id='Job-Description__Section'>
                    <h2
                      style={{
                        color: `${hslaColor(form.jobDescriptionPage?.bodyColor, '#3a3a3a')}`,
                      }}>
                      About the IT Coordinator position
                    </h2>
                    <p
                      style={{
                        color: `${hslaColor(form.jobDescriptionPage?.bodyColor, '#3a3a3a')}`,
                      }}>
                      We are looking for an experienced IT coordinator to help us maintaining company's information technology
                      systems and networks. Your duties will also include technical and administrative tasks to ensure
                      functionality and efficiency of computer and telecom systems.
                    </p>
                  </div>
                </div>
                <div
                  className='job_form'
                  style={{
                    border: `1px solid ${hslaColor(form.jobDescriptionPage?.formBorderColor, '#fff')}`,
                  }}>
                  <h2
                    className='text_heading'
                    style={{
                      color: `${hslaColor(form.jobDescriptionPage?.bodyColor, '#3a3a3a')}`,
                    }}>
                    Submit your application
                  </h2>
                  <div className='simple_form new_candidate' id='new_candidate'>
                    <div className='shophire_input'>
                      <label
                        className='input_label'
                        style={{
                          color: `${hslaColor(form.jobDescriptionPage?.bodyColor, '#3a3a3a')}`,
                        }}>
                        First Name
                      </label>
                      <input className='input_field' required='required' type='text' name='candidate[name]' id='candidate_name' />
                    </div>

                    <div className='shophire_input'>
                      <label
                        className='input_label'
                        style={{
                          color: `${hslaColor(form.jobDescriptionPage?.bodyColor, '#3a3a3a')}`,
                        }}>
                        Last Name
                      </label>
                      <input
                        className='input_field'
                        required='required'
                        type='text'
                        name='candidate[last_name]'
                        id='candidate_last_name'
                      />
                    </div>

                    <div className='shophire_input'>
                      <label
                        className='input_label'
                        style={{
                          color: `${hslaColor(form.jobDescriptionPage?.bodyColor, '#3a3a3a')}`,
                        }}>
                        Email
                      </label>
                      <input
                        className='input_field'
                        required='required'
                        type='text'
                        name='candidate[email]'
                        id='candidate_email'
                      />
                    </div>

                    <div className='shophire_input'>
                      <label
                        className='input_label'
                        style={{
                          color: `${hslaColor(form.jobDescriptionPage?.bodyColor, '#3a3a3a')}`,
                        }}>
                        Phone{' '}
                      </label>
                      <input
                        className='input_field'
                        required='required'
                        type='text'
                        name='candidate[phone]'
                        id='candidate_phone'
                      />
                    </div>

                    <div className='shophire_input'>
                      <label
                        className='input_label'
                        style={{
                          color: `${hslaColor(form.jobDescriptionPage?.bodyColor, '#3a3a3a')}`,
                        }}>
                        Cover Letter (Optional)
                      </label>
                      <textarea
                        className='input_field'
                        aria-invalid='false'
                        aria-multiline='true'
                        rows='5'
                        style={{ width: '100%' }}
                        name='candidate[cover_letter]'
                        id='candidate_cover_letter'></textarea>
                    </div>

                    <input
                      type='button'
                      name='commit'
                      value='Submit Application'
                      className='btn form_button'
                      style={{
                        backgroundColor: `${hslaColor(form.jobDescriptionPage?.buttonColor, '#3a3a3a')}`,
                        color: `${hslaColor(form.jobDescriptionPage?.buttonTextColor, '#fff')}`,
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </Modal>
        </Page>
        {toastMarkup}
      </Frame>
    </AppProvider>
  );
};

export default JobDescriptionPage;
